import React from 'react';

import { cn } from '@/lib/utils';

import Card from '@/decodo/components/atoms/Card';
import LinkButton from '@/decodo/components/atoms/LinkButton';
import { Text } from '@/decodo/components/atoms/Text';
import { VideoPopup } from '@/decodo/components/atoms/videoPopup';

import IconHelpDocs from '~/icons/file-question-03.svg';
import IconPlayButton from '~/icons/play-circle.svg';

export interface ProductCardItemProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  description: React.ReactNode;
  button: React.ReactNode;
  videoLink: React.ReactNode;
  documentLink?: string;
}

export default function ProductCardItem({
  icon,
  title,
  subtitle,
  description,
  button,
  videoLink,
  documentLink,
}: ProductCardItemProps) {
  return (
    <Card
      classNames={{
        root: 'bg-base-white h-full dark:bg-gray-dark-900 dark:border-gray-dark-600',
      }}
    >
      <div className='flex h-full flex-col justify-between'>
        <div className='text-left'>
          <div className='pb-4'>{icon}</div>
          <div className='pb-3'>
            <Text
              color='gray-950'
              variant='subtitle-l-d'
              classNames={{ wrapper: 'font-serif' }}
            >
              {title}
            </Text>
            <Text
              color='gray-950'
              variant='subtitle-l-d'
              classNames={{ wrapper: 'font-serif' }}
            >
              {subtitle}
            </Text>
          </div>
          <Text
            color='gray-500'
            classNames={{
              wrapper: cn('dark:text-gray-dark-400', !!button && 'pb-6'),
            }}
          >
            {description}
          </Text>
        </div>
        {!!button && (
          <div className='flex h-10 items-center gap-6'>
            {!!button && (
              <div data-tracker-component='action-button'>{button}</div>
            )}
            {videoLink && (
              <VideoPopup
                renderVideo={(props) =>
                  React.cloneElement(videoLink as React.ReactElement, props)
                }
                icon={<IconPlayButton className='text-base-black' />}
                classNames={{
                  root: 'p-1 w-8 !h-8 bg-transparent hover:bg-gray-200/40',
                }}
              />
            )}
            {!!documentLink && (
              <LinkButton
                data-tracker-component='help-docs'
                variant='filled'
                href={documentLink}
                slim
                classNames={{
                  root: 'p-1 w-8 h-8 rounded-3xl bg-transparent hover:bg-gray-100 text-base-black',
                }}
              >
                <IconHelpDocs className='w-6' />
              </LinkButton>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
